// =============================================================================
// NUDGE POLICY — the crickets/follow-up silence thresholds as DATA-IN-DB.
//
// The CONFIG pond for L-crickets (docs/ARCHITECTURE-generation-loops.md §4,
// docs/briefs/launch-week-briefs.md §A W1-7): how many days of silence on an
// `applied` application before a follow-up nudge is OFFERED (never auto-sent,
// never auto-moved — the antithesis law). One row = one tunable tier set, so
// Jason can retune the cadence WITHOUT a deploy — a row UPDATE, never a code
// change. Mirrors grounding_policy.ts / brevity_budgets.ts byte-for-byte:
// singleton-by-key, folds toward defaults, a Source resolves it and NEVER throws.
//
// Cross-user (NO userId) — one global cadence today; per-user override is a
// future additive column if ever needed, not built here (no signal calls for it
// yet). `key` is 'default' so a future named policy (e.g. a per-bucket cadence)
// can land as a second row without a shape change.
//
// Additive only (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool,
// mirrored here, exported from index.ts. Nothing dropped or renamed.
// =============================================================================

import { boolean, index, integer, pgTable, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const nudgePolicy = pgTable(
  'nudge_policy',
  {
    id: id(),
    /** The policy key — 'default' today; room for a future named override. UNIQUE
     *  so a re-seed/recalibration upserts in place (ON CONFLICT) instead of duplicating. */
    key: varchar('key', { length: 24 }).notNull().default('default'),
    /** Days of silence on an `applied` application before the FIRST nudge tier
     *  ('gentle') is offered. The brief's default: 7. */
    tier1Days: integer('tier1_days').notNull().default(7),
    /** Days of silence before the SECOND tier ('firm') supersedes tier 1. Default: 14. */
    tier2Days: integer('tier2_days').notNull().default(14),
    /** Days of silence before the THIRD tier ('stale') — where a "mark closed?"
     *  offer joins the nudge (still OFFERED, never auto-moved). Default: 21. */
    tier3Days: integer('tier3_days').notNull().default(21),
    /** Row toggle — an inactive row is treated as absent, never silently applied. */
    active: boolean('active').notNull().default(true),
    ...timestamps,
  },
  (t) => ({
    keyIdx: index('nudge_policy_key_idx').on(t.key),
    keyUq: uniqueIndex('nudge_policy_key_uq').on(t.key),
  }),
);
